/**
 * The dialogs (part 11 log, table D): settings, "delete forever", "empty the trash", the split, the help (part 11b), the
 * word count (part 19b), the notes (part 19c), the DOCX export's look (part 22) and Share a reading link (part 38d). One native modal
 * `<dialog>`, which keeps the keyboard focus inside while open, closes on Escape and returns the focus where it was.
 */
import { createEffect, createMemo, createSignal, onCleanup, untrack } from 'solid-js';
import type { SplitPoint } from '@cockatrice/core';
import { el, each } from '../ui/dom.ts';
import { closesOnBackdrop, ModalBody, ModalButtons, ModalHead } from './modal.ts';
import { DialogPanel } from '../ui/Dialog.ts';
import { component } from '../ui/solid.ts';
import type { Child } from '../ui/dom.ts';
import Button from '../ui/Button.ts';
import Checkbox from '../ui/Checkbox.ts';
import { focusAfterRemoval } from './chapter-list.ts';
import { focusOn } from './announce.ts';
import { focusHome } from './home.ts';
import { DocxChoice } from './docx.ts';
import { HelpView } from './help.ts';
import { BundleImportReview, BundleUpdateReview } from './bundle-import.ts';
import { ImportReview } from './import.ts';
import { ReimportReview } from './reimport.ts';
import { BookWindow } from './book-window.ts';
import { WordsView } from './words.ts';
import { AccountSheet } from './account-sheet.ts';
import { InviteDialog } from './invite-dialog.ts';
import { ShareDialog } from './share-dialog.ts';
import { LockSheet, refocusLock } from './backup-lock.ts';
import { LockedBundleSheet } from './locked-import.ts';
import type { Dialog, Shell } from './shell.ts';

/** The dialogs that are the design's sheets or modals, which name themselves (each its own heading). */
type SheetKind = 'account' | 'share' | 'invite' | 'backup-lock' | 'locked-bundle' | 'new-book' | 'book';

/** Whether `dialog` names itself (its kind a {@link SheetKind}). */
function namesItself(dialog: Dialog): dialog is Extract<Dialog, { readonly kind: SheetKind }> {
  return ['account', 'share', 'invite', 'backup-lock', 'locked-bundle', 'new-book', 'book'].includes(dialog.kind);
}

/** The heading of a dialog (D2–D4). */
function heading(shell: Shell, dialog: Exclude<Dialog, { readonly kind: SheetKind }>): string {
  switch (dialog.kind) {
    case 'delete':
      return shell.t('confirm.deleteTitle', { title: dialog.title });
    case 'empty-trash':
      return shell.t('confirm.emptyTitle');
    case 'discard-copy':
      return shell.t('confirm.discardTitle');
    case 'split':
      return shell.t('split.title', { title: dialog.title });
    case 'help':
      return shell.t('guide.title');
    case 'import':
      return shell.t('import.reviewTitle');
    case 'words':
      return shell.t('words.title');
    case 'docx':
      return shell.t('docx.title');
    case 'reimport':
      return shell.t('reimport.reviewTitle', { name: dialog.fileName });
    case 'bundle-import':
      return shell.t('bundleImport.reviewTitle');
    case 'bundle-update':
      return shell.t('bundleImport.updateHeading', { title: dialog.review.title });
  }
}

/**
 * D3: "delete forever", "empty the trash" and "discard kept text": what goes, Cancel (the first focus) and the danger
 * button that names what it destroys (the library's Button).
 */
function Confirm(shell: Shell, dialog: Extract<Dialog, { kind: 'delete' | 'empty-trash' | 'discard-copy' }>): Child {
  const { t, workspace, ui } = shell;
  const confirm = (): void => {
    ui.setDialog(null);
    const done = (): void => {
      focusAfterRemoval(shell);
    };
    if (dialog.kind === 'delete') void workspace.deleteForever(dialog.chapterId).then(done);
    else if (dialog.kind === 'empty-trash') void workspace.emptyTrash().then(done);
    else void workspace.discardCopy(dialog.copyId).then(done);
  };
  const body = (): string => {
    switch (dialog.kind) {
      case 'delete':
        return t('confirm.deleteBody');
      case 'empty-trash':
        return t('confirm.emptyBody', { count: dialog.count });
      case 'discard-copy':
        return t('confirm.discardBody');
    }
  };
  const action = (): string => {
    switch (dialog.kind) {
      case 'delete':
        return t('confirm.deleteAction');
      case 'empty-trash':
        return t('confirm.emptyAction', { count: dialog.count });
      case 'discard-copy':
        return t('confirm.discardAction');
    }
  };
  return [
    ModalBody({}, el('p', {}, body())),
    ModalButtons(
      component(Button, { variant: 'secondary', autofocus: true, onClick: () => ui.setDialog(null), children: t('confirm.cancel') }),
      component(Button, { variant: 'danger', onClick: confirm, children: action() }),
    ),
  ];
}

/** D4: the split: every point with a checkbox (the library's Checkbox), Split and Cancel (C5). */
function Split(shell: Shell, dialog: Extract<Dialog, { kind: 'split' }>): Child {
  const { t, workspace, ui } = shell;
  const [chosen, setChosen] = createSignal<ReadonlySet<number>>(new Set());
  const points = dialog.offer.points;
  // A heading is named by its text, a scene break by its number among the chapter's scene breaks.
  const label = (point: SplitPoint): string =>
    point.kind === 'heading'
      ? t('split.heading', { title: point.title ?? '' })
      : t('split.scene', { number: points.filter((other) => other.kind === 'scene-break' && other.at <= point.at).length });
  const cancel = (): Child => component(Button, { variant: 'secondary', onClick: () => ui.setDialog(null), children: t('split.cancel') });
  if (points.length === 0) return [ModalBody({}, el('p', {}, t('split.none'))), ModalButtons(cancel())];
  return [
    ModalBody(
      {},
      el('p', {}, t('split.body')),
      el(
        'ul',
        { class: 'split-points' },
        each(
          () => points,
          (point) =>
            el(
              'li',
              {},
              component(Checkbox, {
                id: `split-${point.at}`,
                autofocus: point === points[0],
                label: label(point),
                get checked() {
                  return chosen().has(point.at);
                },
                onChange: (checked: boolean) => {
                  const next = new Set(chosen());
                  if (checked) next.add(point.at);
                  else next.delete(point.at);
                  setChosen(next);
                },
              }),
            ),
        ),
      ),
    ),
    ModalButtons(
      cancel(),
      component(Button, {
        variant: 'primary',
        get disabled() {
          return chosen().size === 0;
        },
        onClick: () => {
          ui.setDialog(null);
          void workspace.split(dialog.offer, [...chosen()]).then(shell.focusEditor);
        },
        children: t('split.confirm'),
      }),
    ),
  ];
}

/** The dialog's body for `dialog`. */
function body(shell: Shell, dialog: Dialog): Child {
  switch (dialog.kind) {
    case 'delete':
    case 'empty-trash':
    case 'discard-copy':
      return Confirm(shell, dialog);
    case 'split':
      return Split(shell, dialog);
    case 'help':
      // Part 11g: the head's Close is its one control (MD2); no second Close at its end.
      // The guide's text scrolls inside the window and holds nothing to focus: it takes the focus itself, so the keys can
      // scroll it (2.1.1; axe's scrollable-region-focusable).
      return ModalBody({ tabIndex: 0 }, HelpView(shell));
    case 'import':
      return ImportReview(shell, dialog.review);
    case 'words':
      return WordsView(shell);
    case 'docx':
      return DocxChoice(shell);
    case 'reimport':
      return ReimportReview(shell, dialog.review, dialog.pictures);
    case 'bundle-import':
      return BundleImportReview(shell, dialog.review);
    case 'bundle-update':
      return BundleUpdateReview(shell, dialog.review);
    case 'new-book':
      return BookWindow(shell, 'new');
    case 'book':
      return BookWindow(shell, 'edit', dialog.error);
    case 'account':
      return AccountSheet(shell, dialog.task);
    case 'share':
      return ShareDialog(shell);
    case 'invite':
      return InviteDialog(shell, dialog.person);
    case 'backup-lock':
      return LockSheet(shell, dialog.task);
    case 'locked-bundle':
      return LockedBundleSheet(shell, dialog.file);
  }
}

/**
 * The modal dialog, open while the interface has a dialog to show (D1), with the workspace's `announcers` inside it (part
 * 11c, deep-fix r1: outside an open modal, a live region is hidden from assistive technology).
 */
export function Dialogs(shell: Shell, announcers: () => Child): Child {
  const { ui } = shell;
  const shown = createMemo(() => ui.dialog());
  // Opening and closing follow whether there is a dialog, not which one, so another one replacing it keeps it open.
  const open = createMemo(() => shown() !== null);
  // The dialog last shown, which its close event closes: the Close button and Escape both clear `shown` first.
  let closing: Dialog | null = null;
  createEffect(() => {
    closing = shown() ?? closing;
  });
  return el(
    'dialog',
    {
      // Part 36c: an account action's dialog is the design's sheet.
      // Part 38d: Share a reading link is the design's modal; part 38b: so is sharing with people.
      // Part 41b: so are the backup password's and a locked backup's.
      // Part 11g: one look for every dialog (`modal.ts`); the account's and sharing's keep their own class for their
      // contents' styles, and sharing's is wider.
      // PLAN.md §3, step 3: the library's host for its dialog panel; `dialog` and the kinds' classes stay the hooks.
      class: () => {
        const kind = shown()?.kind ?? '';
        const host = `dialog ui-dialog-host ui-dialog-host--${size(kind)}`;
        if (['account', 'backup-lock', 'locked-bundle'].includes(kind)) return `${host} ac-sheet`;
        if (kind === 'share' || kind === 'invite') return `${host} sh-modal`;
        // Part 11h: New book and Book details, the design's wide window.
        return kind === 'new-book' || kind === 'book' ? `${host} book-window` : host;
      },
      // MD3: a click or tap on the backdrop closes it, as Close and Escape do.
      ...closesOnBackdrop(() => {
        ui.setDialog(null);
      }),
      'aria-labelledby': 'dialog-heading',
      // The dialog opens modal while there is one to show, and closes with it or with the app.
      ref: (node: HTMLDialogElement) => {
        createEffect(() => {
          if (open()) {
            node.showModal();
            // M7: the part that asks for the first focus (a long dialog's heading, a form's safe button) takes it, whatever
            // the browser's own choice for a <dialog> with an [autofocus] inside (Chrome keeps it on the <dialog>).
            // A microtask later: a dialog shown as the app starts is opened before its content is in it.
            queueMicrotask(() => {
              node.querySelector<HTMLElement>('[autofocus]')?.focus();
            });
            // M7 (part 11b, deep-fix r2): a dialog taller than the screen opens at its start, its heading in view,
            // wherever the first focus scrolled it.
            node.scrollTop = 0;
          } else node.close();
        });
        onCleanup(() => {
          node.close();
        });
      },
      onClose: () => {
        ui.setDialog(null);
        ui.setCapturing(null);
        // V11: the help that opened by itself had nothing focused to go back to; the caret goes into the text, or on the
        // home page (part 11d, R5) to its heading.
        if (closing?.kind === 'help' && closing.first) {
          if (ui.route() === 'write') shell.focusEditor();
          else focusHome();
        }
        // Part 18, V6: a review closed any way but Import book stores nothing.
        if (closing?.kind === 'import') shell.importer.closeReview();
        // Part 23b, V′6: so does a re-import's, closed any way but Apply.
        if (closing?.kind === 'reimport') shell.reimporter.closeReview();
        // Part 24c, Y3: so does a bundle's.
        // G5 (deep-fix r5): put away any way but Import book (a review still waiting), the focus on Import bundle….
        if (closing?.kind === 'bundle-import' && shell.restorer.state().kind === 'review') {
          shell.restorer.closeReview();
          focusOn(shell, '.import-bundle');
        }
        // Part 24d, S′7: an update's review closed any way but Apply abandons the staging; G2: its undo on the status's action.
        if (closing?.kind === 'bundle-update' && shell.restorer.state().kind === 'ready') {
          void shell.restorer.discard();
          focusOn(shell, '.bundle-import-action');
        }
        // Part 41b: the backup password's opener may have gone with the lock's change.
        if (closing?.kind === 'backup-lock') refocusLock(shell);
        // E11: a locked backup's sheet put away hands nothing on; the focus back on Import bundle….
        if (closing?.kind === 'locked-bundle' && !shell.restorer.busy()) focusOn(shell, '.import-bundle');
        // Part 19b, B5: a count still to come is dropped.
        if (closing?.kind === 'words') shell.counter.close();
        closing = null;
      },
    },
    component(DialogPanel, {
      get size() {
        return size(shown()?.kind ?? '');
      },
      // The content itself, not a function of it: it is made as `shown` changes, before the effect above opens the
      // dialog, so its [autofocus] is there when showModal() chooses the first focus.
      get children() {
        return content();
      },
    }),
  );

  function content(): Child {
      // Each dialog is made afresh, untracked, whenever another replaces it.
      const dialog = shown();
      return dialog === null
        ? null
        : untrack(() => [
            // M7 (part 11b, deep-fix r2): the help's only control is Close, at its end, where the first focus would
            // scroll it; its heading takes the first focus instead, so it opens at its start, the warnings in view.
            // Part 18 (Y): the review, as tall, opens at its start the same way. Part 19b (Y): so does the word count, whose
            // first control is a goal's field, which on a phone would open the keyboard over the counts; part 19c (Y2): so do
            // the notes, whose first control is the synopsis; part 23b (Y): so does a re-import's review; part 24c (Y3): a bundle's;
            // part 24d (Y′3): an update's.
            // Part 36c: an account sheet names itself, step by step (its own heading).
            // Part 11g: every dialog's head is the modal's (`modal.ts`), Close in it.
            namesItself(dialog) ? null : ModalHead(shell, heading(shell, dialog), { focus: ['help', 'import', 'words', 'reimport', 'bundle-import', 'bundle-update'].includes(dialog.kind) }),
            body(shell, dialog),
            announcers(),
          ]);
  }
}

/** A dialog's width: the book windows and sharing are the wide ones (the design's), the rest the usual. */
function size(kind: string): 'md' | 'lg' {
  return ['new-book', 'book', 'share', 'invite'].includes(kind) ? 'lg' : 'md';
}
